import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { find, findIndex, isEmpty, isEqual, isNaN, isNil, isUndefined, map, sortBy, replace, includes } from "lodash"
import { Area, Bar, Brush, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis, } from 'recharts';
import { gridLine } from "@plannel/components/dashboard/chartTokens";
import ToggleButton from "@plannel/components/ToggleButton";
import { SettingsButton } from "@plannel/components/ActionIconButton";
import ChartCommonConfigModal from "@plannel/components/modal/ChartCommonConfigModal";
import reduxUtil from "@plannel/utils/redux-util";
import numberUtil from "@plannel/utils/number-util";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import tabMenuList from "@plannel/pages/TabMenuList";
import p13nService from "@plannel/services/system/p13n-service";
import CodeUtil from "@plannel/pages/utils/code-util";
import { AgStyleTooltipContent } from "@plannel/components/rechart/AgStyleTooltip";
import chartP13nSignature from "@plannel/components/rechart/chartP13nSignature";
import { FS } from "@plannel/utils/font-util";

/*
 * 막대 폭 상한. **고정 폭이 아니다** — recharts 가 범주 칸 폭에서 계산한 값을 여기서 자르기만 한다.
 *   `barSize={30}` 으로 못 박혀 있던 탓에 화면이 넓어져도 막대는 그대로고 빈칸만 넓어졌다.
 *   (PivotChartComposed 와 같은 규약 — 두 렌더러의 값이 갈리지 않게 함께 고칠 것.)
 */
const MAX_BAR_SIZE = 40;
// 막대 윗모서리. 누적(stackId) 막대에는 주지 않는다 — 중간 토막까지 둥글어져 단이 어긋나 보인다.
const BAR_RADIUS = [4, 4, 0, 0];

/*
 * 점선(DASHED) 대시 패턴 — **앱 전역 단일 출처**.
 * recharts 를 쓰는 차트가 여러 벌이라(ChartComposed / PivotChartComposed / 통합 대시보드 /
 * DW 대시보드) 각자 값을 정하면 같은 "점선"이 화면마다 달라진다. 여기서만 정의하고 가져다 쓴다.
 */
export const DASH_PATTERN = "4 3";
const { colorSet, colorByMeasure } = ColorUtil;

const ComposedBarLineChart = (props) => {
  const {
    data,
    xAxis,
    options,
    dataSeries,
    nullConvert,
    customDataFormatter,
    referenceLine,
    tooltipAnimation,
    toggle,
    customTooltip,
    allDataSeries,
    showConfigurationPopup,
    strokeByField,
    brushStartEndIndex,
    setBrushStartEndIndex,
    multiSeries = true,
    brush = false,
    activeMeasures
  } = props;
  const [chartData, setChartData] = useState([]);
  const [chartSeries, setChartSeries] = useState([]);
  const [chartOrgSeries, setChartOrgSeries] = useState([]);
  const [chartId, setChartId] = useState("");
  const [chartOptions, setChartOptions] = useState(options ?? {});
  const [chartModalResultData, setChartModalResultData] = useState({});

  const tabState = reduxUtil.getTabState();
  const activeTab = tabState.tabKeys[tabState.tabIndex];
  const viewName = tabMenuList.findLv3Menu(activeTab).reduxKey;

  /*
   * 마지막으로 읽은 p13n 응답. 언어 변경은 시리즈의 **번역문만** 바꾸므로 재조회 사유가 아니다.
   * 그릴 데이터(data)와 시리즈 구성(field 목록)이 그대로면 보관본으로 다시 조립한다.
   */
  const p13nCacheRef = useRef({ sig: null, data: null, content: null });
  const seriesSig = chartP13nSignature(viewName, allDataSeries ?? dataSeries);

  const brushObj = {
    active: brush,
    start: brushStartEndIndex?.start ? brushStartEndIndex?.start : undefined,
    end: brushStartEndIndex?.end ? brushStartEndIndex?.end : undefined,
    setBrushStartEndIndex
  }
    

  const baseFormatter = (number) => {
    return numberUtil.formatNumber(number);
  }
  
  const dataToChartData = (p13nData) => {
    const arrExtractedChartData = [];
    const targetSeries = allDataSeries ? allDataSeries : dataSeries;  
    let tempChartId = "";

    targetSeries.forEach((series, idx) => {
      if (idx === targetSeries.length - 1) {
        tempChartId = `${tempChartId}${series.field}Chart`
        setChartId(tempChartId);
      } else {
        tempChartId += series.field;
      }
    });

    if (p13nData.chart?.[tempChartId]) {
      const series = []; 
      const orgSeries = [];
      
      const targetChart = p13nData.chart[tempChartId];
      
      targetSeries.forEach((target) => {
        const p13nSeriesData = targetChart[target.field]; 
        if (p13nSeriesData) {
          const result = {...target, type: p13nSeriesData.type.toLowerCase()}
          orgSeries.push(result);
          if (p13nSeriesData.activeFlg) {
            series.push(result);
          }
        }
      });

      setChartSeries(series);
      // 설정 모달 행 순서의 원본 — **type 으로 정렬하지 않는다.**
      //   정렬하면 차트 타입을 바꿔 저장할 때마다 모달 목록 순서가 뒤바뀐다(모달이 이 순서를 그대로 쓴다).
      setChartOrgSeries(orgSeries);
    } else { 
      const series = [];
      const orgSeries = targetSeries.map((target) => {
        const usedSeries = dataSeries.filter(usedData => usedData.field === target.field);

        if (usedSeries.length === 0) {
          target.activeFlg = false;
        } else {
          series.push(usedSeries[0]);
        }

        return target;
      });

      setChartSeries(series);
      setChartOrgSeries(orgSeries);
    }

    data.forEach((item) => {
      const dataObj = {};

      targetSeries.forEach((series) => {
        if (nullConvert) {
          dataObj[series.text] = item[series.field] ? item[series.field] : 0;
        } else {
          dataObj[series.text] = item[series.field];
        }
      });

      if (!chartOptions?.decimalPoint) {
        for (const entry of Object.entries(dataObj)) {
          const value = entry[1];
          if (isUndefined(Math.round(value)) || isNaN(Math.round(value))) {
            continue;
          }
          dataObj[entry[0]] = Math.round(value);
        }
      }

      arrExtractedChartData.push({
        name: item[xAxis],
        ...dataObj,
        currencySymbol: item?.currency
      })
    });

    return arrExtractedChartData;
  }

  useEffect(() => {
    if (data && data.length > 0) {
      if (dataSeries) {
        const cache = p13nCacheRef.current;
        // 언어만 바뀐 경우 — 네트워크 없이 보관본으로 다시 조립한다.
        if (isEqual(cache.sig, seriesSig) && cache.data === data && !isNil(cache.content)) {
          setChartData(dataToChartData(cache.content));
          return;
        }

        p13nService.get(viewName)
        .then(response => {
          const resultData = response.data;
          p13nCacheRef.current = { sig: seriesSig, data, content: resultData };
          setChartData(dataToChartData(resultData));
        })
        .catch(e => {
          debug(e);
        });
      }
    } else {
      setChartData([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dataSeries, seriesSig]);

  useEffect(() => {
    if (chartModalResultData.reload) {
      // 설정 모달이 저장 직후 내용을 직접 넘겨준다. 보관본도 같이 갱신해 두어야 다음 언어 변경 때
      // 옛 설정으로 되돌아가지 않는다.
      p13nCacheRef.current = { sig: seriesSig, data, content: chartModalResultData.content };
      dataToChartData(chartModalResultData.content);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartModalResultData])

  useEffect(() => {
    const defaultOptions = {
      margin: { left: 10, top: 20, bottom: brush ? 20 : 10, right: 30 },
      xAxisPadding: { left: 50, right: 50 },
      // 라벨은 눕힌다. -10° 는 공간을 아껴 주지도 못하면서 축만 삐뚤어 보이게 했다.
      //   ⚠ 각도만 바꾸고 `xAxisHeight` 는 건드리지 말 것 — 범례가 아래쪽 고정 오프셋이라
      //     이 높이 안에 같이 들어앉는다(PivotChartComposed 에서 줄였다가 라벨이 범례를 파고들었다).
      xAxisAngle: 0,
      xAxisHeight: 45,
      xAxisDy: 10,
      xAxisInterval: "preserveEnd",
      yAxisDomain: [0, 'auto'],
      minWidth: "760px",
      minHeight: "240px"
    }
    if (options) {
      if (options?.mode && isEqual(options.mode, "fill")) {
        const fillModeOptions = {
          margin: { left: 20, top: 20, right: 20 },
          xAxisHeight: 40,
        }
        setChartOptions({ ...defaultOptions, ...fillModeOptions, ...options });
      } else {
        setChartOptions({ ...defaultOptions, ...options });
      }
    } else {
      setChartOptions(defaultOptions);
    }

  }, [options, brush]);

  return (
    <BaseChartContainer 
      chartData={chartData}
      dataSeries={chartSeries} 
      baseFormatter={customDataFormatter ? customDataFormatter : baseFormatter} 
      multiSeries={multiSeries} strokeByField={strokeByField}
      options={chartOptions} 
      referenceLine={referenceLine ? referenceLine : null} 
      tooltipAnimation={tooltipAnimation} 
      toggle={toggle} 
      customTooltip={customTooltip} 
      viewName={viewName} 
      setChartModalResultData={setChartModalResultData} 
      dataOrgSeries={chartOrgSeries} 
      chartId={chartId} 
      allDataSeries={allDataSeries} 
      showConfigurationPopup={showConfigurationPopup} 
      brushObj={brushObj}
      activeMeasures={activeMeasures}
      />
  );
}

// 그라데이션 id — field 에 공백/특수문자가 있으면 url(#..) 참조가 깨져 SVG 가 검정으로 칠해진다.
// 반드시 id-safe 로 정규화한다(defs/fill 양쪽 동일 규칙).
const gradKey = (f) => `caGrad-${String(f).replace(/[^\w-]/g, "_")}`;

const BaseChartContainer = ({ chartData, dataSeries, baseFormatter, options, referenceLine, tooltipAnimation, toggle, customTooltip, viewName, setChartModalResultData, dataOrgSeries, chartId, showConfigurationPopup, multiSeries, strokeByField, brushObj, activeMeasures, allDataSeries }) => {
  const theme = useTheme();
  const [sortedDataSeries, setSortedDataSeries] = useState([]);
  const [formatters, setFormatters] = useState([]);
  const [yAxisInfo, setYAxisInfo] = useState([]);
  const [chartOptions, setChartOptions] = useState(options);
  const [seriesAnanlysisObj, setSeriesAnanlysisObj] = useState({});
  const yAxisList = [];
  /*
   * 색 인덱스 — **차트 타입·활성 여부와 무관해야 한다.**
   * 렌더 배열(sortedDataSeries)은 type 으로 정렬되고(아래 sortBy) activeFlg 로 걸러진 목록이라,
   * 그 순번을 색 인덱스로 쓰면 **차트 타입을 바꾸거나 측정치를 끄는 것만으로 손대지 않은 시리즈까지
   * 색이 한 칸씩 밀린다**(colorByMeasure 는 미등록 measure·비default 팔레트에서 인덱스 색을 쓴다).
   * → 페이지가 선언한 원본 순서에서 뽑는다. 목록에 없으면 기존 순번으로 폴백(동작 보존).
   */
  const colorIndexOf = (field, fallbackIndex) => {
    const orderSeries = isEmpty(allDataSeries) ? dataSeries : allDataSeries;
    const i = findIndex(orderSeries, (s) => s.field === field);
    return i >= 0 ? i : fallbackIndex;
  };
  const referenceX = referenceLine ? referenceLine.x : null;
  const referenceY = referenceLine ? referenceLine.y : null;
  const [toggled, setToggled] = useState(toggle && dataSeries.length > 0 ? dataSeries[0].type : "");
  const [chartConfigOpen, setChartConfigOpen] = useState(false);
  const showPopup = showConfigurationPopup === false ? false : true;
  const currentUserData = JSON.parse(localStorage.getItem("user"));
  const formatterByName = (number, name) => {
    if (!isEmpty(formatters)) {
      const yAxisName = find(yAxisInfo, info => info.datas.includes(name.trim()))?.yAxis;
      const filteredFormatter = formatters.find(formatter => isEqual(formatter.name, yAxisName))?.formatter;
      return isEmpty(yAxisName) ? baseFormatter(number) : filteredFormatter(number);
    }

    return number;
  }

  const formatterByNameForToolTip = (value, name, series) => {
    let result = value;
    if (!isEmpty(formatters)) {
      const yAxisName = isEqual(name, "ALL") ? "ALL" : find(yAxisInfo, info => info.datas.includes(name))?.yAxis;
      const filteredFormatter = formatters.find(formatter => isEqual(formatter.name, yAxisName))?.formatter;
      result =  filteredFormatter ? filteredFormatter(value) : baseFormatter(value);
    }
    let symbol = "";
    if (chartOptions.symbol) {
      symbol = chartOptions.symbol;
    }
    if (chartOptions.isAmt && currentUserData.currencySymbol) {
      symbol = currentUserData.currencySymbol;
    }
    if (series?.payload && series?.payload.currencySymbol && series?.type) {
      series.type = series?.payload.currencySymbol
    }
    if (series.type) {
      symbol = series.type
    }
    if (chartOptions.isAmt) {
      if (series?.payload && series?.payload.currencySymbol) {
        symbol = series?.payload.currencySymbol;
      }
    }
    if (includes(series.name, "%")) {
      symbol = "";
    }
    
    if (includes(result, symbol)) {
      symbol = "";
    }

    if (value < 0) {
      return `-${symbol}${replace(result.toString(), "-", "")}`;
    }
    return symbol+result;
  }

  const setChart = (overrideSeries) => {
    const seriesToProcess = overrideSeries ?? dataSeries;
    const infos = [];
    for (const series of seriesToProcess) {
      const yAxis = series?.groupName ? series.groupName : series.text;
      const idx = findIndex(infos, info => isEqual(info.yAxis, yAxis));
      if (idx > -1) {
        infos[idx].datas.push(series.text);
      } else {
        infos.push({ yAxis, datas: [series.text], seriesFormatter: series?.formatter });
      }
    }
    setYAxisInfo(infos);

    const allFormatter = [];
    for (const info of infos) {
      const { yAxis,  seriesFormatter } = info; // datas,
      // const valuesByGroup = [];
      // for (const data of datas) {
      //   const values = map(chartData, data).filter(val => !isNull(val) && !isUndefined(val));
      //   valuesByGroup.push(...values);
      // }
      const formatter = seriesFormatter ?? numberUtil.formatNumber; // 기본 숫자 포맷
      allFormatter.push({ formatter, name: yAxis });
      // allFormatter.push({ formatter: baseFormatter, name: yAxis });
    }
    setFormatters(allFormatter);
  }

  /* 설정된 데이터를 분석하여 그래프의 Style을 조정하기 위함 (기본 Chart Option Margin 및 Y축의 translate X 축) 
  * Series의 orientation이 한축에 2개이상 존재할 경우 해당 축의 길이를 계산하여 Margin 및 Y축의 translate X 축을 조정한다.
  */
  const LARGE_VALUE_BASE_MARGIN = 30;
  const Y_AXIS_TRANSFORM_DEFUALT = 10;
  const chartAndYAxisStyleWhenSeriesOrientationDuplicate = (chartData, dataSeries) => {
    /* dataSeries 및 ChartData가 존재하지 않는다면 스킵 */
    if (isNil(dataSeries) || isEmpty(dataSeries) || isNil(chartData) || isEmpty(chartData)) return;

    const newChartOptions = { ...options };
    const ananlysisObj = {};

    let idx = 0;
    const groupInfoObj = {};
    const leftRightSumLength = { left: 0, right: 0 }; /* Left, Right 별 총 길이를 구하여 전체적인 Chart margin을 조정하기 위한 변수 */
    const prevLeftRightLength = { left: 0, right: 0 };
    for (const series of dataSeries) {
      /* 기본값은 Index 순서별 left, right, left, right 반복 */
      let orientation = idx % 2 === 0 ? "left" : "right";

      /* 기본값과 별개로 차트 기준축이 존재한다면 덮어쓰기 */
      if (!isEmpty(series?.orientation)) {
        orientation = series.orientation;
      }

      /* 한축에 Group이 묶여있는경우 이미 Group에 셋팅되어있는 축으로 설정. */
      if (!isEmpty(series?.groupName)) {
        if (isEmpty(groupInfoObj?.[series.groupName])) {
          groupInfoObj[series.groupName] = orientation;
        } else {
          orientation = groupInfoObj[series.groupName];
        }
      }

      idx++;
      const seriesText = series.text;
      const seriesMaxValues = Math.max.apply(null, map(chartData, seriesText));

      const maxValue = isNaN(seriesMaxValues) ? "0" : seriesMaxValues.toString();
      const splitDotValue = maxValue.split(".")[0];
      const valueLength = splitDotValue.length;

      /* [yAxisTransform] Left: 음수, Right: 양수, 기본: 0 (Label 및 Tick의 사이즈 때문에 X 축 조정하기 위함)*/
      const yAxisLabelName = series.groupName ? series.groupName : seriesText;
      if (isNil(ananlysisObj[yAxisLabelName]) || isEmpty(ananlysisObj[yAxisLabelName])) {
        ananlysisObj[yAxisLabelName] = {
          orientation,
          valueLength,
          isLarge: valueLength > 7,
          yAxisTransform: 0
        };

        if (orientation === "left") {
          leftRightSumLength.left += valueLength;

          if (prevLeftRightLength.left > 7) {
            const yAxisTransform = -(prevLeftRightLength.left * 2 + Y_AXIS_TRANSFORM_DEFUALT);
            ananlysisObj[yAxisLabelName] = { orientation, valueLength, isLarge: valueLength > 7, yAxisTransform };
          }

          prevLeftRightLength.left = valueLength;
        } else {
          leftRightSumLength.right += valueLength;

          if (prevLeftRightLength.right > 7) {
            const yAxisTransform = (prevLeftRightLength.right * 2 + Y_AXIS_TRANSFORM_DEFUALT);
            ananlysisObj[yAxisLabelName] = { orientation, valueLength, isLarge: valueLength > 7, yAxisTransform };
          }

          prevLeftRightLength.right = valueLength;
        }
      } else {
        const originObj = ananlysisObj[yAxisLabelName];
        const compareValue = originObj.valueLength > valueLength ? originObj.valueLength : valueLength;

        if (orientation === "left") {
          leftRightSumLength.left = leftRightSumLength.left - originObj.valueLength + compareValue;
        } else {
          leftRightSumLength.right = leftRightSumLength.right - originObj.valueLength + compareValue;
        }

        ananlysisObj[yAxisLabelName] = {
          orientation,
          valueLength: compareValue,
          isLarge: compareValue > 7,
          yAxisTransform: originObj.yAxisTransform
        };
      }
    }

    if (leftRightSumLength.left > 7) {
      newChartOptions.margin.left = LARGE_VALUE_BASE_MARGIN + leftRightSumLength.left;
    }

    if (leftRightSumLength.right > 7) {
      newChartOptions.margin.right = LARGE_VALUE_BASE_MARGIN + leftRightSumLength.right;
    }

    setChartOptions(newChartOptions);
    setSeriesAnanlysisObj(ananlysisObj); /* {orientation: text, length: number, isLarge: boolean, yAxisTransform: number} */
  }

  useEffect(() => {
    if (!isEmpty(chartData) && !isEmpty(dataSeries) && !isEmpty(options)) {
      if (toggle) {
        toggle.initial = dataSeries[0].type;
      }

      /* 설정된 데이터를 분석하여 그래프의 Style을 조정하기 위함 (기본 Chart Option Margin 및 Y축의 translate X 축)
      * Series의 orientation이 한축에 2개이상 존재할 경우 해당 축의 길이를 계산하여 Margin 및 Y축의 translate X 축을 조정한다.
      */
      const effectiveSeries = activeMeasures && activeMeasures.length > 0
        ? dataSeries.filter(s => activeMeasures.includes(s.field))
        : dataSeries;
      chartAndYAxisStyleWhenSeriesOrientationDuplicate(chartData, effectiveSeries);

      setSortedDataSeries(sortBy(effectiveSeries, ["type"]));
      setChart(effectiveSeries);
    } else {
      setSortedDataSeries([]);
      setChart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, dataSeries, options, activeMeasures]);

  useEffect(() => {
    if (toggle && sortedDataSeries) {
      for (const row of sortedDataSeries) {
        if (toggle?.except) {
          if (!toggle.except.includes(row.field)) {
            row.type = toggled;
          }
        } else {
          row.type = toggled;
        }
      }
      if (toggle?.initial) {
        toggle.initial = null;
      }
    }
    setChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggled]);

  const handleChartConfigOpen = () => {
    setChartConfigOpen(!chartConfigOpen);
  };

  const isFilled = chartOptions?.mode && isEqual(chartOptions.mode, "fill");
  const CustomTooltip = customTooltip;
  if (chartData && sortedDataSeries) {
    const orientationList = [];
    const applyLeftRightTranslateX = { left: 0, right: 0 };

    return (
      <Box sx={{position: "relative", height:"100%"}}>
        <Box sx={{position: "absolute", right: "0%", top: "0%", zIndex: 99}}>
          {showPopup ? <SettingsButton name="chartConfig" label="chartConfig" onClick={handleChartConfigOpen} /> : null}
          {toggle?.type ? <ToggleButton toggleType="chart" initialValue={toggle?.initial ? toggle.initial : null} toggleValue={setToggled}></ToggleButton> : <></>}
        </Box>
      {/* <ToggleButton toggleType= toggleValue={setToggled}></ToggleButton>  */}
      <ResponsiveContainer className={viewName.replace(/_/g, "-").toLowerCase()} width="98%" height="100%" minWidth={chartOptions.minWidth} minHeight={chartOptions.minHeight}>
        <ComposedChart
          data={chartData}
          margin={chartOptions.margin}
        >
          <defs>
            {sortedDataSeries.map((series, index) => {
              if (series.type !== "area") return null;
              // 그라데이션 색은 area 의 선(stroke)과 반드시 같아야 한다.
              //   선은 typeOptions.fill/stroke(예: ABC-XYZ 색)를 쓰는데, 여기서 colorByMeasure 로만 계산하면
              //   면과 선의 색이 어긋난다(알 수 없는 field 는 기본 팔레트로 떨어짐) → 시리즈 지정색을 우선.
              const to = series?.typeOptions || {};
              const gc = to.fill || to.stroke || colorByMeasure(series.field, colorIndexOf(series.field, index)) || theme.palette.primary.main;
              return (
                <linearGradient key={gradKey(series.field)} id={gradKey(series.field)} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={gc} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={gc} stopOpacity={0} />
                </linearGradient>
              );
            })}
          </defs>
          {/* 세로 격자는 끈다 — 범주축이라 칸 경계는 막대가 이미 말해 주고, 선이 늘면 표처럼 보인다. */}
          <CartesianGrid stroke={gridLine(theme)} vertical={false} />
          <XAxis dataKey="name" interval={chartOptions.xAxisInterval} fontSize={FS.H2} height={chartOptions.xAxisHeight} angle={chartOptions.xAxisAngle} dy={chartOptions.xAxisDy} dx={chartOptions.xAxisDx} tickLine={false} />
          {
            sortedDataSeries.map((series, index) => {
              let position = "top";
              const angle = 0;
              let dy = -5;
              let orientation = index % 2 === 0 ? "left" : "right";
              const yAxisLabel = series?.groupName ? series.groupName : series.text;
              if (yAxisList.indexOf(yAxisLabel) !== -1) {
                return "";
              }
              yAxisList.push(yAxisLabel);

              if (isNil(series?.orientation) || isEmpty(series?.orientation)) {
                if (index >= 1 && sortedDataSeries[index - 1].axisHide) {
                  if (orientation === "left") {
                    orientation = "right";
                  } else if (orientation === "right") {
                    orientation = "left";
                  }
                }
              }

              let yAxisLabelColor = theme.palette.text.primary;
              if (isFilled && !series?.groupName) {
                let labelColor = colorSet(colorIndexOf(series.field, index));
                if (series?.typeOptions?.fill || series?.typeOptions?.stroke) {
                  const { fill, stroke } = series.typeOptions;
                  labelColor = fill || stroke;
                } else {
                  labelColor = colorByMeasure(series.field, colorIndexOf(series.field, index));
                }

                yAxisLabelColor = labelColor;
              }
              
              const obj = seriesAnanlysisObj?.[yAxisLabel]; /* {yAxisTransform: number} */
              const applyOrientation = series.orientation ? series.orientation : orientation;
              /* 축이 여러개 존재하는지 판단하는 로직 Y축 이름이 길면 Text가 겹칠 수 있으므로 하단에 출력  */
              const grpNumber = orientationList.filter(orit => orit === applyOrientation).length % 2;
              if (grpNumber === 1) {
                position = "bottom";
                dy = 5;
              }
              orientationList.push(applyOrientation);

              let translateX = 0;
              if (!isNil(obj) && !isEmpty(obj)) {
                const { yAxisTransform } = obj;
                const cumulateTransform = applyOrientation === "left" ? applyLeftRightTranslateX.left : applyLeftRightTranslateX.right;
                translateX = yAxisTransform + cumulateTransform;
                if (applyOrientation === "left") {
                  applyLeftRightTranslateX.left += yAxisTransform;

                  /* 왼쪽에 위치한다면 translateX 는 음수값이 되어야함. */
                  if (translateX > 0) {
                    translateX = -translateX;
                  }
                } else {
                  applyLeftRightTranslateX.right += yAxisTransform;
                  
                  /* 오른쪽에 위치한다면 translateX 는 양수값이 되어야함. */
                  if (translateX < 0) {
                    translateX = Math.abs(translateX);
                  }
                }
              }

              let labelDX = applyOrientation === "left" ? 15 : -15;
              if (translateX !== 0) {
                labelDX = translateX / 2;
              }

              const referenceXPosition = referenceX?.position ? referenceX.position : "top";
              const referenceYPosition = referenceY?.position ? referenceY.position : "top";

              return [
                <YAxis label={{ value: yAxisLabel, angle, dy, dx: labelDX, position, fontSize: FS.SUB, fill: yAxisLabelColor }}
                  yAxisId={yAxisLabel}
                  fontSize={FS.BODY}
                  domain={chartOptions.yAxisDomain}
                  tickFormatter={number => formatterByName(number, series.text)}
                  orientation={series.orientation ? series.orientation : orientation}
                  key={yAxisLabel}
                  unit={chartOptions.unit}
                  hide={series.axisHide ? series.axisHide : false}
                  style={{ transform: `translate(${translateX}px, 0px)` }}
                />,
                <ReferenceLine key={`${yAxisLabel}_Line`} y={0} yAxisId={yAxisLabel} stroke="#999999" />,
                <ReferenceLine key={`${yAxisLabel}_ReferenceLineX`} x={referenceX ? referenceX.value : null} label={{ value: referenceX ? referenceX.title : null, position: referenceXPosition, fill: "#595959", fontSize: FS.SUB }} yAxisId={yAxisLabel} stroke="#8884d8" fontSize={5} />,
                <ReferenceLine key={`${yAxisLabel}_ReferenceLineY`} y={referenceY ? referenceY.value : null} label={{ value: referenceY ? referenceY.title : null, position: referenceYPosition, fill: "#595959", fontSize: FS.SUB }} yAxisId={yAxisLabel} stroke="red" />
              ];

            })
          }
          {customTooltip ? <Tooltip isAnimationActive={tooltipAnimation} offset={-60} content={<CustomTooltip/>} /> : <Tooltip isAnimationActive={tooltipAnimation} formatter={formatterByNameForToolTip} offset={-60}
            contentStyle={options?.tooltipContentStyle}
            content={<AgStyleTooltipContent />} />}
          {isFilled ? <></> : < Legend />}
          {sortedDataSeries.map((series, index) => {
            const yAxisLabel = series?.groupName ? series.groupName : series.text;
            const seriesColor = colorByMeasure(series.field, colorIndexOf(series.field, index));
            const strokeWidth = strokeByField ? strokeByField(series.field) : 1.5;
            const typeOptions = series?.typeOptions;
            const typeOptionOjbect = {...typeOptions};

            if (series.type === "bar") {
            if (Object.keys(typeOptionOjbect).includes("stroke")) {
                typeOptionOjbect.fill = typeOptionOjbect.stroke;
              }
              return (
                <Bar yAxisId={yAxisLabel}
                  dataKey={series.text}
                  maxBarSize={MAX_BAR_SIZE}
                  radius={Object.keys(typeOptionOjbect).includes("stackId") ? undefined : BAR_RADIUS}
                  fill={seriesColor}
                  key={series.field}
                  tooltipType={CodeUtil.amtCurrencyMeasure.includes(series.field) ? currentUserData.currencySymbol : ""}
                  {...typeOptionOjbect} />
              )
            } else if (series.type === "area") {
              if (Object.keys(typeOptionOjbect).includes("stroke") || Object.keys(typeOptionOjbect).includes("fill")) {
                const color = Object.keys(typeOptionOjbect).includes("stroke") ? typeOptionOjbect.stroke : typeOptionOjbect.fill;
                typeOptionOjbect.fill = color;
                typeOptionOjbect.stroke = color;
              }
              delete typeOptionOjbect.fill;   // 그라데이션 fill 이 이기도록 solid fill 제거
              return (
                <Area
                  yAxisId={yAxisLabel}
                  dataKey={series.text}
                  stroke={seriesColor}
                  key={series.field}
                  tooltipType={CodeUtil.amtCurrencyMeasure.includes(series.field) ? currentUserData.currencySymbol : ""}
                  {...typeOptionOjbect}
                  fill={`url(#${gradKey(series.field)})`}
                  fillOpacity={1} />
              )
            } else {
              if (Object.keys(typeOptionOjbect).includes("fill")) {
                typeOptionOjbect.stroke = typeOptionOjbect.fill;
              }
              // dashed = 점선 라인. 목표·기준선 성격 시리즈를 실적선과 눈으로 구분하려는 것이라
              // line 과 렌더가 같고 대시 패턴만 다르다(패턴은 DASH_PATTERN 단일 출처).
              return (
                <Line yAxisId={yAxisLabel}
                  dataKey={series.text}
                  type="linear"
                  activeDot={{ r: 4 }}
                  dot={{ r: 1.5, fill: seriesColor, strokeWidth: 0 }}
                  stroke={seriesColor}
                  strokeWidth={strokeWidth ?? 1}
                  strokeDasharray={series.type === "dashed" ? DASH_PATTERN : undefined}
                  key={series.field}
                  tooltipType={CodeUtil.amtCurrencyMeasure.includes(series.field) ? currentUserData.currencySymbol : ""}
                  {...typeOptionOjbect} />
              )
            }
          })}
            {
              brushObj?.active ? <Brush
                dataKey={"name"}
                height={20}
                stroke={theme.palette.primary.main}
                startIndex={brushObj?.start}
                endIndex={brushObj?.end}
                onChange={(e) => {
                  const setMetohd = brushObj?.setBrushStartEndIndex;
                  if (setMetohd) {
                    brushObj?.setBrushStartEndIndex({ start: e.startIndex, end: e.endIndex })
                  }                  
                }}
              /> : ""
            }
        </ComposedChart>
      </ResponsiveContainer>
      <ChartCommonConfigModal
        open={chartConfigOpen}
        setOpen={handleChartConfigOpen}
        viewName={viewName}
        dataSeries={dataOrgSeries}
        colorOrderSeries={allDataSeries}
        modalResultData={setChartModalResultData}
        chartId={chartId}
        multiSeries={multiSeries}
      />
      </Box>
    );
  }

  return <div />;
}

export default React.memo(ComposedBarLineChart, isEqual);